import React, { useEffect, useState } from 'react'
import { Card, Spinner } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import axios from 'axios'

const useWindowSize = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handler = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return width;
};

const ClientMonthlyCountsChart = () => {
    const [fetchedData, setFetchedData] = useState("")
    const [renderJsxStatus, setRenderJsxStatus] = useState(false)
    const width = useWindowSize();
    const isMobile = width < 768;
    const [reRenderComponent, setReRenderComponent] = useState(false)
    const [loader, setLoader] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const clientsPerPage = 15

    const [comparisonMode, setComparisonMode] = useState('auto')
    const [customMonthA, setCustomMonthA] = useState(0)
    const [customMonthB, setCustomMonthB] = useState(1)
    
    window.addEventListener("storage", (e) => {
        setReRenderComponent(!reRenderComponent)
    });

    // Load saved page from localStorage on mount
    useEffect(() => {
        const savedPage = localStorage.getItem("clientChartCurrentPage");
        if (savedPage) {
            setCurrentPage(parseInt(savedPage));
        }
    }, []);

    // Large, diverse dark color palette with 100 unique vibrant colors
    const colorPalette = [
        '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0',
        '#546E7A', '#26a69a', '#D10CE8', '#F9A825', '#2E93fA',
        '#66DA26', '#E91E63', '#FF9800', '#00BCD4', '#9C27B0',
        '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
        '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
        '#3D4E81', '#8B4C9F', '#E74C3C', '#16A085', '#D35400',
        '#2980B9', '#9B59B6', '#C0392B', '#27AE60', '#E67E22',
        '#34495E', '#8E44AD', '#A93226', '#1E8449', '#D68910',
        '#2471A3', '#7D3C98', '#922B21', '#196F3D', '#B9770E',
        '#1B4F72', '#6C3483', '#78281F', '#117A65', '#D4AC0D',
        '#154360', '#5B2C9F', '#641E16', '#0E6655', '#B8860B',
        '#2E86AB', '#BB6BCE', '#CA6F1E', '#186A3B', '#F39C12',
        '#2874A6', '#A76FA0', '#A04000', '#1B7A2E', '#D48A10',
        '#2C3E50', '#9E44AD', '#C2392B', '#229954', '#E57E22',
        '#1A5276', '#76448A', '#B93226', '#296F3D', '#D45400',
        '#254360', '#7C3483', '#922B31', '#127A65', '#DA6F1E',
        '#21618C', '#AF7AC5', '#CB4335', '#2E8449', '#D78910',
        '#3884A6', '#884EA0', '#B04000', '#148F77', '#D97706',
        '#3B4F72', '#8C3483', '#741E16', '#1E6655', '#C8860B',
        '#377EA8', '#A569BD', '#E84C3C', '#26A085', '#E45400',
        '#4E86C1', '#AB59B6', '#D0392B', '#37AE60', '#F67E22',
        '#458FA7', '#7E55B7', '#F64C4C', '#38B1A5', '#F76500',
        '#56A0C2', '#8F66C8', '#D75D5D', '#49C2B6', '#E87611',
        '#67B1D3', '#A077D9', '#E86E6E', '#5AD3C7', '#F98722'
    ];

    // Function to assign unique color to each organization
    const assignColorToOrg = (orgName, allOrgs) => {
        // Create a hash from the organization name
        let hash = 0;
        for (let i = 0; i < orgName.length; i++) {
            hash = orgName.charCodeAt(i) + ((hash << 5) - hash);
        }
        // Use the hash to consistently select a color from the palette
        return colorPalette[Math.abs(hash) % colorPalette.length];
    };

    // Fetch data from API
    useEffect(() => {
        const fetchClientCounts = async () => {
            try {
                setLoader(true);
                const idToken = localStorage.getItem("idTokenBilling");
                
                if (!idToken) {
                    console.log("Waiting for idToken...");
                    return;
                }

                const currentYear = new Date().getFullYear();
                const currentDate = new Date().toISOString().split('T')[0];
                
                const startDate = `${currentYear}-01-01 00:00:00.000`;
                const endDate = `${currentDate} 23:59:59.999`;
                const apiUrl = `https://api.niroggyan.com/billing/smartreport/counts?query_on=counts&query_by=org&start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`;

                const config = {
                    method: 'GET',
                    url: apiUrl,
                    headers: {
                        'Authorization': idToken,
                        'Content-Type': 'application/json'
                    }
                };

                console.log("Making API call to fetch client counts...");
                const response = await axios(config);
                
                console.log("Client counts API response:", response.data);
                
                if (response.data && response.data.isSuccess) {
                    setFetchedData(response.data);
                    setRenderJsxStatus(true);
                    setLoader(false);
                    console.log("Client counts loaded successfully");
                    // Reset to page 1 when new data is fetched
                    setCurrentPage(1);
                    localStorage.setItem("clientChartCurrentPage", "1");
                } else {
                    console.log("API call failed or data not successful:", response.data);
                    setLoader(false);
                }
            } catch (error) {
                console.error("Error fetching client counts:", error);
                setLoader(false);
            }
        };

        fetchClientCounts();
    }, [reRenderComponent]);

    // Transform API data to chart format with pagination
    const transformDataForChart = (apiData) => {
        if (!apiData || !apiData.data) {
            return { seriesData: [], totalClients: 0, totalPages: 0 };
        }

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentYear = new Date().getFullYear().toString();
        
        let seriesData = [];
        let clientTotals = [];

        // Process each client and collect all unique organization names
        const allOrgNames = [];
        apiData.data.forEach((client) => {
            const orgName = client.org;
            if (!allOrgNames.includes(orgName)) {
                allOrgNames.push(orgName);
            }
            
            const counts = client.counts[currentYear] || {};
            
            let monthlyData = [];
            let totalCount = 0;

            months.forEach((month) => {
                const count = counts[month] || 0;
                monthlyData.push(count);
                totalCount += count;
            });

            clientTotals.push({ name: orgName, total: totalCount, data: monthlyData });
        });

        // Create a color map for all organizations ensuring each gets a unique color
        const orgColorMap = {};
        const usedColors = new Set();
        let colorIndex = 0;
        
        // Sort organizations alphabetically to ensure consistent color assignment
        const sortedOrgs = [...allOrgNames].sort();
        
        sortedOrgs.forEach(org => {
            // Try to find a unique color for this org using hash
            let hash = 0;
            for (let i = 0; i < org.length; i++) {
                hash = org.charCodeAt(i) + ((hash << 5) - hash);
            }
            let baseIndex = Math.abs(hash) % colorPalette.length;
            
            // Find the first available color (starting from hash position, or sequential if all taken)
            let finalIndex = baseIndex;
            if (usedColors.has(baseIndex)) {
                // Find next available color
                let found = false;
                for (let i = 0; i < colorPalette.length; i++) {
                    const checkIndex = (baseIndex + i) % colorPalette.length;
                    if (!usedColors.has(checkIndex)) {
                        finalIndex = checkIndex;
                        found = true;
                        break;
                    }
                }
                // If still no color found, use sequential as fallback
                if (!found) {
                    finalIndex = colorIndex % colorPalette.length;
                }
            }
            
            const assignedColor = colorPalette[finalIndex];
            usedColors.add(finalIndex);
            orgColorMap[org] = assignedColor;
            colorIndex++;
        });

        // Sort clients by total count (highest first)
        clientTotals.sort((a, b) => b.total - a.total);

        // Calculate pagination
        const totalClients = clientTotals.length;
        const totalPages = Math.ceil(totalClients / clientsPerPage);
        const startIndex = (currentPage - 1) * clientsPerPage;
        const endIndex = startIndex + clientsPerPage;

        // Get clients for current page
        const paginatedClients = clientTotals.slice(startIndex, endIndex);

        // Map series data with pre-assigned colors
        seriesData = paginatedClients.map((client) => ({
            name: client.name,
            data: client.data,
            color: orgColorMap[client.name] // Use pre-assigned color for consistency
        }));

        return { seriesData, totalClients, totalPages };
    };

    const chartData = renderJsxStatus ? transformDataForChart(fetchedData) : { seriesData: [], totalClients: 0, totalPages: 0 };
    const seriesData = chartData.seriesData || [];
    const totalClientsCount = chartData.totalClients || 0;
    const totalPages = chartData.totalPages || 1;

    // Calculate total count across all clients
    const calculateTotalCount = (data) => {
        if (!data || !data.data) return 0;
        
        let total = 0;
        const currentYear = new Date().getFullYear().toString();
        
        data.data.forEach((client) => {
            const counts = client.counts[currentYear] || {};
            Object.values(counts).forEach((count) => {
                total += count;
            });
        });

        return total;
    };

    const totalCount = calculateTotalCount(fetchedData);

    // Month-to-month comparison: auto (latest vs previous) or custom (user-selected months)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonthIndex = new Date().getMonth();
    const latestCompletedMonthIndex = currentMonthIndex >= 1 ? currentMonthIndex - 1 : -1;
    const prevMonthIndexAuto = latestCompletedMonthIndex >= 1 ? latestCompletedMonthIndex - 1 : -1;
    const canShowMonthComparisonAuto = latestCompletedMonthIndex >= 1 && prevMonthIndexAuto >= 0;

    const monthA = comparisonMode === 'custom' ? customMonthA : latestCompletedMonthIndex;
    const monthB = comparisonMode === 'custom' ? customMonthB : prevMonthIndexAuto;
    const canShowMonthComparison = comparisonMode === 'auto'
        ? canShowMonthComparisonAuto
        : (monthA >= 0 && monthB >= 0 && monthA !== monthB);

    const monthComparisonData = canShowMonthComparison && seriesData.length > 0
        ? seriesData.map((s) => {
            const valMonthA = s.data[monthA] || 0;
            const valMonthB = s.data[monthB] || 0;
            const pct = valMonthA > 0 ? (((valMonthB - valMonthA) / valMonthA) * 100) : (valMonthB > 0 ? 100 : 0);
            return {
                name: s.name,
                monthA: valMonthA,
                monthB: valMonthB,
                delta: pct,
                color: s.color || '#008FFB'
            };
        }).sort((a, b) => b.monthA - a.monthA)
        : [];

    // Pagination handlers
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            localStorage.setItem("clientChartCurrentPage", newPage.toString());
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            localStorage.setItem("clientChartCurrentPage", newPage.toString());
        }
    };

    // Extract colors from series data
    const seriesColors = seriesData.map(s => s.color || '#008FFB');
    const chartHeight = isMobile ? 280 : 350;

    let lineChartData = {
        series: seriesData,
        options: {
            colors: seriesColors,
            chart: {
                id: "ClientMonthlyCounts",
                height: chartHeight,
                type: 'line',
                zoom: {
                    enabled: false
                },
                toolbar: {
                    show: false
                }
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                width: 3,
                curve: 'smooth',
            },
            title: {
                text: '',
                align: 'left'
            },
            legend: {
                position: 'bottom',
                horizontalAlign: 'left',
                fontSize: '11px',
                itemMargin: {
                    horizontal: 12,
                    vertical: 4
                },
                offsetY: 0,
                offsetX: 0,
                onItemClick: {
                    toggleDataSeries: true
                },
                onItemHover: {
                    highlightDataSeries: true
                },
               
            },
            markers: {
                size: 5,
                hover: {
                    sizeOffset: 6
                }
            },
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
                'Oct', 'Nov', 'Dec'],
            xaxis: {
                categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
                    'Oct', 'Nov', 'Dec'],
                labels: {
                    style: {
                        fontSize: isMobile ? '10px' : '12px'
                    }
                }
            },
            yaxis: {
                title: {
                    text: 'Report Count',
                    style: {
                        fontSize: '12px',
                        color: '#7787bc'
                    }
                },
                labels: {
                    style: {
                        fontSize: '12px'
                    }
                }
            },
            tooltip: {
                shared: true,
                intersect: false,
                followCursor: true,
                custom: function({series, seriesIndex, dataPointIndex, w}) {
                    const fullMonthNames = [
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'
                    ];
                    const month = fullMonthNames[dataPointIndex] || `Month ${dataPointIndex + 1}`;
                    const prevMonthName = dataPointIndex > 0 ? fullMonthNames[dataPointIndex - 1] : null;

                    let allData = [];
                    series.forEach((s, index) => {
                        const value = s[dataPointIndex];
                        const prevValue = dataPointIndex > 0 ? (s[dataPointIndex - 1] || 0) : null;
                        const seriesName = w.globals.seriesNames[index];
                        const color = w.globals.colors[index];

                        let vsPrevText = '';
                        if (value !== null && value !== undefined) {
                            if (prevMonthName && prevValue !== null) {
                                const pct = prevValue > 0
                                    ? (((value - prevValue) / prevValue) * 100).toFixed(1)
                                    : (value > 0 ? '100' : '0');
                                const sign = parseFloat(pct) >= 0 ? '+' : '';
                                vsPrevText = ` (vs ${prevMonthName}: ${sign}${pct}%)`;
                            } else if (dataPointIndex === 0) {
                                vsPrevText = ' (first month)';
                            }
                            allData.push({ seriesName, value, color, vsPrevText });
                        }
                    });

                    allData.sort((a, b) => b.value - a.value);

                    let html = `<div style="padding: 10px; background: white; box-shadow: 0 4px 8px rgba(0,0,0,0.1); border-radius: 8px; font-family: inherit; min-width: 280px;">`;
                    html += `<div style="font-weight: 600; font-size: 14px; color: #2c3e50; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 2px solid #e8e8e8;">`;
                    html += `${month} - Client Reports`;
                    html += `</div>`;
                    html += `<table style="width: 100%; border-collapse: collapse;">`;
                    html += `<thead><tr style="border-bottom: 1px solid #e8e8e8;"><th style="padding: 4px 6px; text-align: left; font-weight: 600; color: #666; font-size: 11px;">CLIENT</th><th style="padding: 4px 6px; text-align: right; font-weight: 600; color: #666; font-size: 11px;">REPORTS</th></tr></thead><tbody>`;

                    allData.forEach((item) => {
                        const deltaColor = item.vsPrevText.includes('+') && !item.vsPrevText.includes('+0') ? '#2E7D32' : (item.vsPrevText.includes('-') ? '#C62828' : '#666');
                        html += `<tr style="border-bottom: 1px solid #f5f5f5;">`;
                        html += `<td style="padding: 6px; text-align: left;">`;
                        html += `<span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background-color: ${item.color}; margin-right: 8px;"></span>`;
                        html += `<span style="font-size: 12px; color: #2c3e50;">${item.seriesName}</span>`;
                        html += `</td>`;
                        html += `<td style="padding: 6px; text-align: right;">`;
                        html += `<span style="font-weight: 600; font-size: 12px; color: #2c3e50;">${item.value}</span>`;
                        if (item.vsPrevText) {
                            html += `<span style="font-size: 10px; color: ${deltaColor}; margin-left: 4px;">${item.vsPrevText}</span>`;
                        }
                        html += `</td></tr>`;
                    });

                    html += `</tbody></table>`;
                    html += `</div>`;
                    return html;
                }
            },
            grid: {
                borderColor: '#f1f1f1'
            },
            responsive: [
                {
                    breakpoint: 768,
                    options: {
                        chart: { height: 280 },
                        xaxis: { labels: { style: { fontSize: '10px' } } },
                        yaxis: { labels: { style: { fontSize: '10px' } } },
                        legend: { fontSize: '10px', itemMargin: { horizontal: 8 } }
                    }
                }
            ]
        },
    };

    const renderJSX = () => {
        return (
            <>
                {/* Header Section */}
                <div style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #EEE' }}>
                    <h2 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '500', color: '#000000ff', marginBottom: '8px' }}>
                        Month wise All Clients
                    </h2>
                    <p style={{ fontSize: isMobile ? '14px' : '15px', color: '#546E7A', margin: 0, width: '100%' }}>
                        View monthly report counts for each client and compare performance across any two months. Use Auto mode for latest vs previous month, or Custom mode to select specific months.
                    </p>
                </div>

                <div style={{ display: "inline-flex" }} className="mb-4">
                    <div>
                        <Card style={{ borderRadius: "15px", marginRight: isMobile ? "0" : "30px", width: isMobile ? "100%" : "auto" }} className="customShadowCss">
                            <Card.Body style={{ padding: isMobile ? "12px 16px" : undefined }}>
                                <Card.Title
                                    style={{ fontWeight: "unset", fontSize: isMobile ? "13px" : "14px", width: isMobile ? "100%" : "140px" }}
                                >{"All Clients Total"}</Card.Title>

                                <Card.Subtitle style={{ fontSize: isMobile ? "16px" : undefined }}>
                                    {totalCount} 
                                    <span style={{ fontWeight: "550", color: "#7787bc", fontSize: isMobile ? "11px" : "12.5px" }}>{" Reports"}</span>
                                </Card.Subtitle>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                <div className="mb-4">
                    <Card style={{ padding: isMobile ? "12px" : "16px" }}>
                        <Card.Title style={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: "space-between",
                            alignItems: isMobile ? "stretch" : "center",
                            marginBottom: "10px",
                            gap: isMobile ? 10 : 0
                        }}>
                            <span style={{ fontSize: isMobile ? "14px" : undefined }}>{"Month wise all clients"}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: isMobile ? "12px" : "13px", flexWrap: "wrap" }}>
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    style={{
                                        padding: isMobile ? "6px 10px" : "4px 12px",
                                        fontSize: isMobile ? "11px" : "12px",
                                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                        backgroundColor: currentPage === 1 ? "#e0e0e0" : "#007bff",
                                        color: currentPage === 1 ? "#999" : "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        opacity: currentPage === 1 ? 0.5 : 1
                                    }}
                                >
                                    &#8249; Prev
                                </button>
                                <span style={{ fontSize: "12px", color: "#666" }}>
                                    Page {currentPage} of {totalPages} ({totalClientsCount} clients)
                                </span>
                                <button
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        padding: "4px 12px",
                                        fontSize: "12px",
                                        cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                                        backgroundColor: currentPage === totalPages ? "#e0e0e0" : "#007bff",
                                        color: currentPage === totalPages ? "#999" : "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        opacity: currentPage === totalPages ? 0.5 : 1
                                    }}
                                >
                                    Next &#8250;
                                </button>
                            </div>
                        </Card.Title>

                        {/* Toggle controls: Comparison mode */}
                        <div style={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            flexWrap: "wrap",
                            gap: isMobile ? 10 : 12,
                            marginBottom: "16px",
                            alignItems: isMobile ? "stretch" : "center"
                        }}>
                            {/* Comparison mode: Auto | Custom */}
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap"
                            }}>
                                <div style={{
                                    display: "flex",
                                    backgroundColor: "#F5F7FA",
                                    padding: "4px",
                                    borderRadius: "12px",
                                    border: "1px solid #E3F2FD"
                                }}>
                                    <button
                                        onClick={() => setComparisonMode('auto')}
                                        style={{
                                            padding: isMobile ? "6px 12px" : "8px 16px",
                                            borderRadius: "8px",
                                            border: "none",
                                            background: comparisonMode === 'auto' ? "#1565C0" : "transparent",
                                            color: comparisonMode === 'auto' ? "white" : "#78909C",
                                            fontWeight: "600",
                                            fontSize: isMobile ? "12px" : "13px",
                                            cursor: "pointer",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        Auto
                                    </button>
                                    <button
                                        onClick={() => setComparisonMode('custom')}
                                        style={{
                                            padding: isMobile ? "6px 12px" : "8px 16px",
                                            borderRadius: "8px",
                                            border: "none",
                                            background: comparisonMode === 'custom' ? "#1565C0" : "transparent",
                                            color: comparisonMode === 'custom' ? "white" : "#78909C",
                                            fontWeight: "600",
                                            fontSize: isMobile ? "12px" : "13px",
                                            cursor: "pointer",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        Custom
                                    </button>
                                </div>
                                {comparisonMode === 'custom' && (
                                    <>
                                        <select
                                            value={customMonthA}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                setCustomMonthA(val);
                                                if (val === customMonthB) setCustomMonthB((val + 1) % 12);
                                            }}
                                            style={{
                                                padding: isMobile ? "6px 8px" : "8px 12px",
                                                borderRadius: "8px",
                                                border: "1px solid #E0E0E0",
                                                fontSize: isMobile ? "12px" : "13px",
                                                fontWeight: "600",
                                                color: "#455A64",
                                                cursor: "pointer"
                                            }}
                                        >
                                            {months.map((m, i) => (
                                                <option key={m} value={i}>Month A: {m}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={customMonthB}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                setCustomMonthB(val);
                                                if (val === customMonthA) setCustomMonthA((val + 1) % 12);
                                            }}
                                            style={{
                                                padding: isMobile ? "6px 8px" : "8px 12px",
                                                borderRadius: "8px",
                                                border: "1px solid #E0E0E0",
                                                fontSize: isMobile ? "12px" : "13px",
                                                fontWeight: "600",
                                                color: "#455A64",
                                                cursor: "pointer"
                                            }}
                                        >
                                            {months.map((m, i) => (
                                                <option key={m} value={i} disabled={i === customMonthA}>Month B: {m}</option>
                                            ))}
                                        </select>
                                    </>
                                )}
                            </div>
                        </div>

                        <Card.Subtitle>
                            {loader ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <ReactApexChart
                                    options={lineChartData.options}
                                    series={lineChartData.series}
                                    type="line"
                                    height={chartHeight}
                                />
                            )}
                        </Card.Subtitle>
                    </Card>
                </div>

                {canShowMonthComparison && monthComparisonData.length > 0 && (
                    <div className="mb-4">
                        <Card style={{ padding: isMobile ? "12px" : "16px", border: "1px solid #E3F2FD" }}>
                            <Card.Title style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "600", color: "#455A64", marginBottom: "12px" }}>
                                {months[monthA]} vs {months[monthB]} {comparisonMode === 'custom' ? '(Custom)' : '(Month-to-Month)'}
                            </Card.Title>
                            <div style={{
                                display: "flex",
                                flexWrap: isMobile ? "nowrap" : "wrap",
                                gap: "10px",
                                overflowX: isMobile ? "auto" : "visible",
                                WebkitOverflowScrolling: isMobile ? "touch" : "auto",
                                paddingBottom: isMobile ? "4px" : 0
                            }}>
                                {monthComparisonData.map((item) => {
                                    const isPositive = item.delta > 0;
                                    const isNegative = item.delta < 0;
                                    const deltaColor = isPositive ? "#2E7D32" : isNegative ? "#C62828" : "#666";
                                    const deltaSign = item.delta > 0 ? "+" : "";
                                    return (
                                        <div
                                            key={item.name}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                padding: isMobile ? "6px 10px" : "8px 12px",
                                                backgroundColor: "#FAFAFA",
                                                borderRadius: "8px",
                                                borderLeft: `4px solid ${item.color}`,
                                                minWidth: isMobile ? 140 : 180,
                                                flexShrink: 0
                                            }}
                                        >
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: isMobile ? "11px" : "12px", fontWeight: "600", color: "#263238", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {item.name}
                                                </div>
                                                <div style={{ fontSize: isMobile ? "10px" : "11px", color: "#78909C" }}>
                                                    {item.monthA.toLocaleString()} → {item.monthB.toLocaleString()}
                                                    <span style={{ color: deltaColor, fontWeight: "600", marginLeft: "4px" }}>
                                                        ({deltaSign}{item.delta.toFixed(1)}%)
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    </div>
                )}
            </>
        )
    }

    return (
        <span>
            {renderJsxStatus || loader ? renderJSX() : ""}
        </span>
    )
}

export default ClientMonthlyCountsChart

